import { useState, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { LoadingSpinner } from '../components/LoadingSpinner';

const API_BASE = import.meta.env.VITE_API_BASE;

function slugify(name: string) {
  return name.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-').slice(0, 50);
}

interface FormData {
  church_name: string; subdomain: string; church_address: string;
  geofence: { latitude: number; longitude: number; radius: number } | null;
  admin_first_name: string; admin_last_name: string;
  admin_email: string; admin_phone: string;
  admin_password: string; confirm_password: string;
  services: { name: string; weekday: number; start_time: string; duration_minutes: number }[];
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function ChurchRegistrationPage() {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [registrationId, setRegistrationId] = useState<string | null>(null);
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [form, setForm] = useState<FormData>({
    church_name: '', subdomain: '', church_address: '',
    geofence: null,
    admin_first_name: '', admin_last_name: '',
    admin_email: '', admin_phone: '',
    admin_password: '', confirm_password: '',
    services: [{ name: 'Sunday Morning Service', weekday: 0, start_time: '10:00', duration_minutes: 90 }]
  });

  const set = (field: keyof FormData, value: any) => {
    setForm(p => ({ ...p, [field]: value })); setError(null);
  };

  const getLocation = () => {
    setIsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { set('geofence', { latitude: pos.coords.latitude, longitude: pos.coords.longitude, radius: 100 }); setIsLoading(false); },
      () => { setError('Unable to get location. Please allow location access and try again.'); setIsLoading(false); }
    );
  };

  /* ── Step 1 Validation ── */
  const validateStep1 = () => {
    if (!form.church_name.trim()) { setError('Church name is required'); return false; }
    if (!form.subdomain) { setError('Subdomain is required'); return false; }
    if (!/^[a-z0-9-]+$/.test(form.subdomain)) { setError('Subdomain: only lowercase letters, numbers, hyphens'); return false; }
    if (!form.geofence) { setError('Church location is required. Please click "Capture Church GPS Coordinates".'); return false; }
    return true;
  };

  /* ── Step 2 Validation & Submit to API ── */
  const validateStep2 = () => {
    if (!form.admin_first_name || !form.admin_last_name) { setError('First and last name required'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.admin_email)) { setError('Valid email required'); return false; }
    if (form.admin_password.length < 8) { setError('Password must be at least 8 characters'); return false; }
    if (form.admin_password !== form.confirm_password) { setError('Passwords do not match'); return false; }
    return true;
  };

  const submitStep2 = async () => {
    if (!validateStep2()) return;
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/register/register`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          church_name: form.church_name, subdomain: form.subdomain,
          admin_first_name: form.admin_first_name, admin_last_name: form.admin_last_name,
          admin_email: form.admin_email, admin_phone: form.admin_phone || undefined,
          church_address: form.church_address || undefined,
          latitude: form.geofence?.latitude, longitude: form.geofence?.longitude,
        })
      });
      const data = await res.json();
      if (data.success) { setRegistrationId(data.request_id); setStep(3); }
      else setError(data.message || 'Registration failed');
    } catch (e: any) { setError(e.message || 'Network error'); }
    setIsLoading(false);
  };

  /* ── OTP handlers ── */
  const handleDigit = (i: number, val: string) => {
    const d = val.replace(/\D/g, '').slice(-1);
    const nd = [...digits]; nd[i] = d; setDigits(nd);
    if (d && i < 5) inputRefs.current[i + 1]?.focus();
  };
  const handleKey = (i: number, e: KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) { inputRefs.current[i - 1]?.focus(); }
  };
  const handlePaste = (e: ClipboardEvent) => {
    const t = e.clipboardData?.getData('text').replace(/\D/g, '').slice(0, 6) || '';
    if (t) { const nd = t.split('').concat(Array(6).fill('')).slice(0, 6); setDigits(nd); inputRefs.current[Math.min(t.length, 5)]?.focus(); }
    e.preventDefault();
  };
  const otp = digits.join('');

  const verifyOtp = async () => {
    if (otp.length !== 6) { setError('Enter all 6 digits'); return; }
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/register/verify-email`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: otp }) });
      const data = await res.json();
      if (data.success) setStep(4);
      else setError(data.message || 'Invalid code');
    } catch (e: any) { setError(e.message); }
    setIsLoading(false);
  };

  const resendCode = async () => {
    setResendCooldown(true); setIsLoading(true);
    try {
      await fetch(`${API_BASE}/register/resend-code`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ admin_email: form.admin_email }) });
    } catch { }
    setIsLoading(false);
    setTimeout(() => setResendCooldown(false), 60000);
  };

  /* ── Final Submit ── */
  const finalSubmit = async () => {
    if (!registrationId) { setError('Registration ID missing'); return; }
    setIsLoading(true);
    try {
      const cr = await fetch(`${API_BASE}/register/complete`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ request_id: registrationId, admin_password: form.admin_password }) });
      const cd = await cr.json();
      if (!cd.success) { setError(cd.message || 'Completion failed'); setIsLoading(false); return; }

      const lr = await fetch(`${API_BASE}/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: form.admin_email, password: form.admin_password }) });
      const ld = await lr.json();
      const token = ld.accessToken;
      if (token) localStorage.setItem('token', token);

      if (token) {
        await fetch(`${API_BASE}/church/onboard`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ contact_email: form.admin_email, contact_phone: form.admin_phone || undefined, address: form.church_address || undefined, geofence: form.geofence, services: form.services })
        });
      }
      window.location.href = '/';
    } catch (e: any) { setError(e.message || 'Network error'); }
    setIsLoading(false);
  };

  /* ── Service helpers ── */
  const addService = () => set('services', [...form.services, { name: '', weekday: 0, start_time: '10:00', duration_minutes: 60 }]);
  const removeService = (i: number) => set('services', form.services.filter((_, idx) => idx !== i));
  const updateService = (i: number, f: string, v: any) => {
    const s = [...form.services]; s[i] = { ...s[i], [f]: v }; set('services', s);
  };

  /* ── Care Atlas Editorial Brand Panel (lg+) ── */
  const BrandPanel = () => (
    <div
      className="hidden lg:flex flex-col justify-between h-full p-10 xl:p-12 text-white relative overflow-hidden"
      style={{
        background: '#192631',
        borderRight: '1px solid rgba(221, 220, 211, 0.14)',
      }}
    >
      {/* Background cartographic dot matrix */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          backgroundImage: 'radial-gradient(circle, rgba(244, 182, 64, 0.12) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          opacity: 0.45,
        }}
      />

      {/* Subtle warm amber ambient bloom */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '-10%',
          left: '-10%',
          width: '420px',
          height: '420px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(244, 182, 64, 0.14) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: '-15%',
          right: '-15%',
          width: '380px',
          height: '380px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(123, 166, 139, 0.12) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Content wrapper */}
      <div className="relative z-10">
        {/* Brand header */}
        <div className="flex items-center gap-3.5 mb-10">
          <div
            style={{
              width: 46,
              height: 46,
              background: 'var(--marigold)',
              borderRadius: '13px 13px 13px 3px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 22px rgba(244, 182, 64, 0.35)',
              flexShrink: 0,
            }}
          >
            <svg width="24" height="24" fill="none" stroke="var(--ink)" strokeWidth="2.2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 21c-4.97 0-9-4.03-9-9 0-4.418 3.582-8 8-8 1.66 0 3.2.5 4.5 1.37L12 10h8c0 6.075-4.925 11-8 11z" />
              <circle cx="9" cy="9" r="1.5" fill="var(--ink)" />
            </svg>
          </div>
          <div>
            <span
              className="text-2xl font-bold tracking-tight text-[#FFFDF8]"
              style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.04em' }}
            >
              Shephard
            </span>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="signal-dot--live" />
              <span
                className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#94A4B0]"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                Care Atlas · Registry
              </span>
            </div>
          </div>
        </div>

        {/* Hero title with italic Fraunces emphasis */}
        <div className="mb-8">
          <span
            className="inline-block px-3 py-1 mb-3 rounded-full text-[9px] font-bold tracking-[0.14em] uppercase"
            style={{
              background: 'rgba(244, 182, 64, 0.14)',
              color: '#F4B640',
              border: '1px solid rgba(244, 182, 64, 0.28)',
              fontFamily: 'var(--font-body)',
            }}
          >
            01 / CHURCH ONBOARDING
          </span>
          <h2
            className="text-3xl xl:text-[36px] font-semibold leading-[1.18] text-[#FFFDF8] mb-3"
            style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.04em' }}
          >
            Safe check-ins for <em className="italic text-[#F4B640] font-normal">every child.</em>
          </h2>
          <p
            className="text-[#9DB0BD] text-sm xl:text-[15px] leading-relaxed max-w-md"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            A secure, simple check-in system built for churches — so you can focus on what matters.
          </p>
        </div>

        {/* 4 Feature Items with Care Atlas Editorial styling */}
        <div className="space-y-3">
          {/* Feature 1: Geofenced check-ins */}
          <div
            className="p-3.5 rounded-2xl flex items-start gap-3.5 transition-all duration-200 hover:bg-white/[0.06]"
            style={{
              background: 'rgba(255, 253, 248, 0.035)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '14px 14px 28px 14px',
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: 'rgba(244, 182, 64, 0.15)',
                border: '1px solid rgba(244, 182, 64, 0.3)',
                borderRadius: '11px 11px 11px 3px',
              }}
            >
              <svg className="w-5 h-5 text-[#F4B640]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[9px] font-bold tracking-[0.14em] uppercase text-[#F4B640]" style={{ fontFamily: 'var(--font-body)' }}>
                01 / PRESENCE VERIFIED
              </span>
              <h3 className="font-semibold text-sm text-[#FFFDF8] mt-0.5" style={{ fontFamily: 'var(--font-body)' }}>
                Geofenced check-ins
              </h3>
              <p className="text-xs text-[#8FA1AD] mt-0.5 leading-snug">
                Parents check in only when physically present at church
              </p>
            </div>
          </div>

          {/* Feature 2: Secure & private */}
          <div
            className="p-3.5 rounded-2xl flex items-start gap-3.5 transition-all duration-200 hover:bg-white/[0.06]"
            style={{
              background: 'rgba(255, 253, 248, 0.035)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '14px 14px 28px 14px',
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: 'rgba(123, 166, 139, 0.16)',
                border: '1px solid rgba(123, 166, 139, 0.35)',
                borderRadius: '11px 11px 11px 3px',
              }}
            >
              <svg className="w-5 h-5 text-[#7BA68B]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[9px] font-bold tracking-[0.14em] uppercase text-[#7BA68B]" style={{ fontFamily: 'var(--font-body)' }}>
                02 / ZERO-KNOWLEDGE
              </span>
              <h3 className="font-semibold text-sm text-[#FFFDF8] mt-0.5" style={{ fontFamily: 'var(--font-body)' }}>
                Secure & private
              </h3>
              <p className="text-xs text-[#8FA1AD] mt-0.5 leading-snug">
                All family data is encrypted and never shared
              </p>
            </div>
          </div>

          {/* Feature 3: One-tap experience */}
          <div
            className="p-3.5 rounded-2xl flex items-start gap-3.5 transition-all duration-200 hover:bg-white/[0.06]"
            style={{
              background: 'rgba(255, 253, 248, 0.035)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '14px 14px 28px 14px',
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: 'rgba(141, 191, 208, 0.16)',
                border: '1px solid rgba(141, 191, 208, 0.35)',
                borderRadius: '11px 11px 11px 3px',
              }}
            >
              <svg className="w-5 h-5 text-[#8DBFD0]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[9px] font-bold tracking-[0.14em] uppercase text-[#8DBFD0]" style={{ fontFamily: 'var(--font-body)' }}>
                03 / RAPID HANDOVER
              </span>
              <h3 className="font-semibold text-sm text-[#FFFDF8] mt-0.5" style={{ fontFamily: 'var(--font-body)' }}>
                One-tap experience
              </h3>
              <p className="text-xs text-[#8FA1AD] mt-0.5 leading-snug">
                Families check in their children in seconds
              </p>
            </div>
          </div>

          {/* Feature 4: Live attendance */}
          <div
            className="p-3.5 rounded-2xl flex items-start gap-3.5 transition-all duration-200 hover:bg-white/[0.06]"
            style={{
              background: 'rgba(255, 253, 248, 0.035)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '14px 14px 28px 14px',
            }}
          >
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: 'rgba(207, 105, 78, 0.16)',
                border: '1px solid rgba(207, 105, 78, 0.35)',
                borderRadius: '11px 11px 11px 3px',
              }}
            >
              <svg className="w-5 h-5 text-[#CF694E]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-[9px] font-bold tracking-[0.14em] uppercase text-[#CF694E]" style={{ fontFamily: 'var(--font-body)' }}>
                04 / REAL-TIME RADAR
              </span>
              <h3 className="font-semibold text-sm text-[#FFFDF8] mt-0.5" style={{ fontFamily: 'var(--font-body)' }}>
                Live attendance
              </h3>
              <p className="text-xs text-[#8FA1AD] mt-0.5 leading-snug">
                See who's here right now from your dashboard
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Brand panel footer */}
      <div className="relative z-10 pt-6 border-t border-white/10 flex items-center justify-between text-xs text-[#7A8C98]">
        <span style={{ fontFamily: 'var(--font-body)' }}>© {new Date().getFullYear()} Shephard</span>
        <span className="flex items-center gap-2 font-medium" style={{ fontFamily: 'var(--font-body)' }}>
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--marigold)]" />
          Built for churches
        </span>
      </div>
    </div>
  );

  /* ── Mobile header strip — shown below lg ── */
  const MobileHeader = () => (
    <div className="lg:hidden flex items-center justify-between px-2 pt-2 pb-6 mb-2">
      <div className="flex items-center gap-3">
        <div
          style={{
            width: 40,
            height: 40,
            background: 'var(--marigold)',
            borderRadius: '11px 11px 11px 3px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(244, 182, 64, 0.3)',
          }}
        >
          <svg width="20" height="20" fill="none" stroke="var(--ink)" strokeWidth="2.2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 21c-4.97 0-9-4.03-9-9 0-4.418 3.582-8 8-8 1.66 0 3.2.5 4.5 1.37L12 10h8c0 6.075-4.925 11-8 11z" />
            <circle cx="9" cy="9" r="1.5" fill="var(--ink)" />
          </svg>
        </div>
        <div>
          <span className="text-xl font-bold text-ink" style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.04em' }}>
            Shephard
          </span>
          <span className="block text-[9px] font-bold tracking-[0.14em] uppercase text-ink-faint">
            Care Atlas
          </span>
        </div>
      </div>
      <a href="/" className="text-xs font-bold text-[var(--ink-soft)] hover:text-[var(--ink)]">
        Sign in →
      </a>
    </div>
  );

  /* ── Care Atlas Step Indicator ── */
  const steps = [
    { label: 'Church Details', hint: 'Basics' },
    { label: 'Your Account', hint: 'Credentials' },
    { label: 'Verify Email', hint: 'OTP code' },
    { label: 'Service Times', hint: 'Schedule' },
  ];

  const StepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between gap-1">
        {steps.map((s, i) => {
          const num = i + 1;
          const done = step > num;
          const active = step === num;
          return (
            <div key={s.label} className="flex-1 flex flex-col items-center text-center">
              <div
                className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-200 ${
                  done
                    ? 'bg-[var(--leaf-soft)] text-[#2F7A4E] border border-[rgba(123,166,139,0.35)]'
                    : active
                    ? 'bg-[var(--marigold)] text-[var(--ink)] shadow-[0_4px_14px_rgba(244,182,64,0.35)] ring-4 ring-[var(--marigold-soft)]'
                    : 'bg-white border border-[var(--border)] text-[var(--ink-faint)]'
                }`}
                style={{ fontFamily: 'var(--font-body)' }}
              >
                {done ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  `0${num}`
                )}
              </div>
              <span
                className={`text-[9px] font-bold tracking-[0.12em] uppercase mt-2 hidden sm:block ${
                  active ? 'text-[var(--ink)]' : done ? 'text-[#2F7A4E]' : 'text-[var(--ink-faint)]'
                }`}
                style={{ fontFamily: 'var(--font-body)' }}
              >
                {s.label}
              </span>
            </div>
          );
        })}
      </div>
      {/* Wayfinding progress hairline */}
      <div className="w-full bg-[var(--border)] h-1 rounded-full mt-3 overflow-hidden">
        <div
          className="h-full bg-[var(--marigold)] transition-all duration-300 ease-out rounded-full"
          style={{ width: `${((step - 1) / (steps.length - 1)) * 100}%` }}
        />
      </div>
    </div>
  );

  const Err = () => error ? (
    <div className="alert-error text-xs sm:text-sm mt-3">
      <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
      <span>{error}</span>
    </div>
  ) : null;

  /* ── STEP 1 ── */
  const Step1 = () => (
    <div className="space-y-5 animate-slide-up">
      <div>
        <span className="mini-label text-[var(--marigold)] font-bold">01 / CHURCH PROFILE</span>
        <h1 className="text-2xl sm:text-[26px] font-bold mt-1 text-[var(--ink)]" style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.04em' }}>
          About your church
        </h1>
        <p className="text-[var(--ink-soft)] text-xs sm:text-sm mt-1" style={{ fontFamily: 'var(--font-body)' }}>
          Let's establish your congregation's identity and geofenced perimeter.
        </p>
      </div>

      <div>
        <label className="label">Church Name *</label>
        <input
          className="input"
          placeholder="Hope Community Church"
          value={form.church_name}
          onChange={(e) => {
            const v = (e.target as HTMLInputElement).value;
            setForm(p => ({ ...p, church_name: v, subdomain: slugify(v) }));
            setError(null);
          }}
        />
      </div>

      <div>
        <label className="label">Your Church Portal URL *</label>
        <div className="flex rounded-xl overflow-hidden">
          <input
            className="input rounded-r-none border-r-0 font-mono text-sm"
            placeholder="hope-community"
            value={form.subdomain}
            onChange={(e) => set('subdomain', (e.target as HTMLInputElement).value.toLowerCase())}
          />
          <span className="px-3.5 py-2.5 bg-[#EDE9DF] border border-[var(--border)] rounded-r-xl text-xs font-semibold text-[var(--ink-soft)] whitespace-nowrap flex items-center">
            .shephard.app
          </span>
        </div>
        <p className="field-hint">Auto-generated from church name. Parents will visit this URL to register.</p>
      </div>

      <div>
        <label className="label">Church Address <span className="label-optional">(optional)</span></label>
        <input
          className="input"
          placeholder="123 Faith Avenue, City, Country"
          value={form.church_address}
          onChange={(e) => set('church_address', (e.target as HTMLInputElement).value)}
        />
      </div>

      <div>
        <label className="label">Church Geofence Location *</label>
        <p className="field-hint mb-2">Required for physical check-ins. Parents must be on-site to register arrivals.</p>
        {form.geofence ? (
          <div
            className="p-4 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            style={{
              background: 'var(--leaf-soft)',
              border: '1px solid rgba(123, 166, 139, 0.3)',
            }}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-xs">
                <svg className="w-5 h-5 text-[var(--leaf)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="signal-dot--live" />
                  <p className="text-xs font-bold uppercase tracking-wider text-[#2F7A4E]">Location Captured</p>
                </div>
                <p className="text-xs text-[var(--ink-soft)] font-mono mt-0.5">
                  {form.geofence.latitude.toFixed(5)}, {form.geofence.longitude.toFixed(5)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 self-end sm:self-center">
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-[#2F7A4E] font-bold whitespace-nowrap">Radius:</label>
                <input
                  type="number"
                  min="50"
                  max="500"
                  className="input py-1 px-2 text-xs w-20 bg-white"
                  value={form.geofence.radius}
                  onChange={(e) => set('geofence', { ...form.geofence!, radius: parseInt((e.target as HTMLInputElement).value) || 100 })}
                />
                <span className="text-xs text-[#2F7A4E]">m</span>
              </div>
              <button type="button" onClick={getLocation} className="text-xs text-[#2F7A4E] font-bold underline hover:opacity-80">
                Update
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={getLocation}
            disabled={isLoading}
            className="w-full flex items-center justify-center gap-2.5 px-4 py-3.5 border-2 border-dashed border-[var(--border)] rounded-xl text-[var(--ink-soft)] hover:border-[var(--marigold)] hover:text-[var(--ink)] hover:bg-[var(--marigold-soft)]/20 transition-all duration-200 disabled:opacity-50"
          >
            {isLoading ? (
              <LoadingSpinner size="small" />
            ) : (
              <svg className="w-5 h-5 text-[var(--marigold)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
            <span className="font-semibold text-sm">
              {isLoading ? 'Capturing GPS coordinates…' : 'Capture Church GPS Coordinates'}
            </span>
          </button>
        )}
      </div>

      <Err />

      <button onClick={() => { if (validateStep1()) setStep(2); }} className="btn-primary btn-lg w-full mt-2">
        Continue to Account Setup →
      </button>
    </div>
  );

  /* ── STEP 2 ── */
  const Step2 = () => (
    <div className="space-y-5 animate-slide-up">
      <div>
        <span className="mini-label text-[var(--marigold)] font-bold">02 / ADMINISTRATOR PROFILE</span>
        <h1 className="text-2xl sm:text-[26px] font-bold mt-1 text-[var(--ink)]" style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.04em' }}>
          Your administrator account
        </h1>
        <p className="text-[var(--ink-soft)] text-xs sm:text-sm mt-1" style={{ fontFamily: 'var(--font-body)' }}>
          This primary credential will oversee operations and safety rosters.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label">First Name *</label>
          <input className="input" placeholder="Sarah" value={form.admin_first_name} onChange={(e) => set('admin_first_name', (e.target as HTMLInputElement).value)} />
        </div>
        <div>
          <label className="label">Last Name *</label>
          <input className="input" placeholder="Wanjiku" value={form.admin_last_name} onChange={(e) => set('admin_last_name', (e.target as HTMLInputElement).value)} />
        </div>
      </div>

      <div>
        <label className="label">Official Email Address *</label>
        <input className="input" type="email" placeholder="admin@hopecommunity.org" value={form.admin_email} onChange={(e) => set('admin_email', (e.target as HTMLInputElement).value)} />
        <p className="field-hint">A 6-digit security code will be sent here for verification.</p>
      </div>

      <div>
        <label className="label">Phone <span className="label-optional">(optional)</span></label>
        <input className="input" type="tel" placeholder="+254 700 000 000" value={form.admin_phone} onChange={(e) => set('admin_phone', (e.target as HTMLInputElement).value)} />
      </div>

      <div>
        <label className="label">Password *</label>
        <input className="input" type="password" placeholder="At least 8 characters" value={form.admin_password} onChange={(e) => set('admin_password', (e.target as HTMLInputElement).value)} />
      </div>

      <div>
        <label className="label">Confirm Password *</label>
        <input className="input" type="password" placeholder="Repeat your password" value={form.confirm_password} onChange={(e) => set('confirm_password', (e.target as HTMLInputElement).value)} />
      </div>

      <Err />

      <div className="flex gap-3 pt-2">
        <button onClick={() => setStep(1)} className="btn-secondary btn-lg flex-1">
          Back
        </button>
        <button onClick={submitStep2} disabled={isLoading} className="btn-primary btn-lg flex-[2]">
          {isLoading ? <LoadingSpinner size="small" /> : null} Send Verification Code →
        </button>
      </div>
    </div>
  );

  /* ── STEP 3 ── */
  const Step3 = () => (
    <div className="space-y-6 animate-slide-up">
      <div className="text-center">
        <div
          style={{
            width: 56,
            height: 56,
            background: 'var(--marigold-soft)',
            borderRadius: '13px 13px 13px 3px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 4px 16px rgba(244, 182, 64, 0.25)',
          }}
        >
          <svg className="w-7 h-7 text-[#7D5507]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 8.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <span className="mini-label text-[var(--marigold)] font-bold">03 / VERIFICATION</span>
        <h1 className="text-2xl sm:text-[26px] font-bold mt-1 text-[var(--ink)]" style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.04em' }}>
          Check your email
        </h1>
        <p className="text-[var(--ink-soft)] text-xs sm:text-sm mt-1 max-w-sm mx-auto" style={{ fontFamily: 'var(--font-body)' }}>
          We dispatched a 6-digit one-time code to <strong className="text-[var(--ink)] font-semibold">{form.admin_email}</strong>
        </p>
      </div>

      <div>
        <label className="label text-center block mb-3">Enter 6-Digit Code</label>
        <div className="flex gap-2 justify-center" onPaste={handlePaste as any}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => { inputRefs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              className={`otp-digit ${d ? 'otp-digit-filled' : ''}`}
              onChange={(e) => handleDigit(i, (e.target as HTMLInputElement).value)}
              onKeyDown={(e) => handleKey(i, e as unknown as KeyboardEvent)}
            />
          ))}
        </div>
      </div>

      <Err />

      <button onClick={verifyOtp} disabled={isLoading || otp.length !== 6} className="btn-primary btn-lg w-full">
        {isLoading ? <LoadingSpinner size="small" /> : null} Verify Security Code →
      </button>

      <div className="text-center pt-1">
        <button
          onClick={resendCode}
          disabled={isLoading || resendCooldown}
          className="text-xs font-semibold text-[var(--ink-soft)] hover:text-[var(--ink)] underline disabled:opacity-50 disabled:no-underline"
        >
          {resendCooldown ? 'Please wait 60s before resending' : 'Didn’t receive it? Resend code'}
        </button>
      </div>
    </div>
  );

  /* ── STEP 4 ── */
  const Step4 = () => (
    <div className="space-y-5 animate-slide-up">
      <div>
        <span className="mini-label text-[var(--marigold)] font-bold">04 / SERVICE SCHEDULE</span>
        <h1 className="text-2xl sm:text-[26px] font-bold mt-1 text-[var(--ink)]" style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.04em' }}>
          Set up your services
        </h1>
        <p className="text-[var(--ink-soft)] text-xs sm:text-sm mt-1" style={{ fontFamily: 'var(--font-body)' }}>
          Define recurring service times when check-in stations and parent rosters will be open.
        </p>
      </div>

      <div className="space-y-3">
        {form.services.map((s, i) => (
          <div
            key={i}
            className="p-4 rounded-xl border border-[var(--border)] bg-white space-y-3 relative"
            style={{ borderRadius: '14px 14px 28px 14px' }}
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold tracking-[0.14em] uppercase text-[var(--coral)]" style={{ fontFamily: 'var(--font-body)' }}>
                0{i + 1} / SERVICE WINDOW
              </span>
              {form.services.length > 1 && (
                <button onClick={() => removeService(i)} className="text-xs font-semibold text-[var(--coral)] hover:underline">
                  Remove
                </button>
              )}
            </div>

            <div>
              <label className="label">Service Name *</label>
              <input
                className="input"
                placeholder="Sunday Morning Service"
                value={s.name}
                onChange={(e) => updateService(i, 'name', (e.target as HTMLInputElement).value)}
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <div>
                <label className="label">Day</label>
                <select className="input" value={s.weekday} onChange={(e) => updateService(i, 'weekday', parseInt((e.target as HTMLSelectElement).value))}>
                  {DAYS.map((d, di) => <option key={di} value={di}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Start Time</label>
                <input className="input" type="time" value={s.start_time} onChange={(e) => updateService(i, 'start_time', (e.target as HTMLInputElement).value)} />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="label">Duration (min)</label>
                <input className="input" type="number" min="15" max="300" value={s.duration_minutes} onChange={(e) => updateService(i, 'duration_minutes', parseInt((e.target as HTMLInputElement).value))} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <button type="button" onClick={addService} className="btn-secondary w-full text-xs font-semibold py-2.5">
        + Add Another Service
      </button>

      <Err />

      <button onClick={finalSubmit} disabled={isLoading} className="btn-primary btn-lg w-full mt-2">
        {isLoading ? <LoadingSpinner size="small" /> : null} Launch Church Dashboard →
      </button>
    </div>
  );

  return (
    <div
      className="min-h-screen flex flex-col lg:flex-row"
      style={{
        background: 'var(--parchment)',
        minHeight: '100vh',
      }}
    >
      {/* Brand panel — takes 42% on lg+, sticky top */}
      <div className="lg:flex-none lg:w-[42%] lg:sticky lg:top-0 lg:h-screen">
        <BrandPanel />
      </div>

      {/* Form panel */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-8 lg:px-12 xl:px-16 py-8 sm:py-12 overflow-y-auto">
        <MobileHeader />
        <div className="max-w-lg w-full mx-auto">
          <div
            className="p-6 sm:p-9 relative"
            style={{
              background: 'var(--paper)',
              border: '1px solid var(--border)',
              borderRadius: '18px 18px 48px 18px',
              boxShadow: 'var(--shadow-card)',
            }}
          >
            {/* Tilted Marigold tape accent */}
            <div className="card-tape" />

            <StepIndicator />
            {step === 1 && <Step1 />}
            {step === 2 && <Step2 />}
            {step === 3 && <Step3 />}
            {step === 4 && <Step4 />}
          </div>

          <p className="text-center text-xs text-[var(--ink-soft)] mt-6" style={{ fontFamily: 'var(--font-body)' }}>
            Already registered?{' '}
            <a href="/" className="font-bold text-[var(--ink)] hover:text-[#7D5507] underline transition-colors">
              Sign in to dashboard →
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
